export type BbanErrorCode =
  | 'INVALID_INPUT'
  | 'INVALID_IBAN_FORMAT'
  | 'INVALID_PREFIX'
  | 'INVALID_ACCOUNT_NUMBER';

export interface BbanResult {
  isValid: boolean;
  bankCode?: string;
  prefix?: string;
  accountNumber?: string;
  formattedBban?: string;
  error?: BbanErrorCode;
}

export const validateCzechWeight = (numberStr: string): boolean => {
  const weights = [6, 3, 7, 9, 10, 5, 8, 4, 2, 1];
  const padded = numberStr.padStart(10, '0');
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(padded[i], 10) * weights[i];
  }
  return sum % 11 === 0;
};

export const isValidCzechIban = (iban: string): boolean => {
  if (!iban || typeof iban !== 'string') return false;
  const code = iban.toUpperCase().replace(/\s/g, '');
  if (!/^CZ\d{22}$/.test(code)) return false;

  // Move CZ + check digits to the end and convert letters to numbers (C=12, Z=35)
  const rearranged = `${code.substring(4)}1235${code.substring(2, 4)}`;

  // Modulo 97 check
  return BigInt(rearranged) % 97n === 1n;
};

export const ibanToBbanCz = (iban: string): BbanResult => {
  if (!iban || typeof iban !== 'string') {
    return { isValid: false, error: 'INVALID_INPUT' };
  }

  const cleanIban = iban.toUpperCase().replace(/\s/g, '');

  if (!isValidCzechIban(cleanIban)) {
    return { isValid: false, error: 'INVALID_IBAN_FORMAT' };
  }

  const bankCode = cleanIban.substring(4, 8);
  const prefix = cleanIban.substring(8, 14);
  const accountNumber = cleanIban.substring(14, 24);

  // Validate prefix and account number using Modulo 11
  if (!validateCzechWeight(prefix)) {
    return {
      isValid: false,
      error: 'INVALID_PREFIX',
    };
  }

  if (!validateCzechWeight(accountNumber)) {
    return {
      isValid: false,
      error: 'INVALID_ACCOUNT_NUMBER',
    };
  }

  // Format BBAN: [prefix]-[accountNumber]/[bankCode]
  const parsedPrefix = parseInt(prefix, 10);
  const formattedPrefix = parsedPrefix === 0 ? '' : `${parsedPrefix}-`;
  const formattedAccountNumber = parseInt(accountNumber, 10).toString();
  const formattedBban = `${formattedPrefix}${formattedAccountNumber}/${bankCode}`;

  return {
    isValid: true,
    bankCode,
    prefix,
    accountNumber,
    formattedBban,
  };
};

/**
 * Formats a number amount with thousands separators and decimal places
 * @param amount - The numeric amount to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string with non-breaking spaces as thousands separator and comma as decimal separator
 * @example formatAmount(130000) => "130 000,00"
 * @example formatAmount(1250.5) => "1 250,50"
 */
export const formatAmount = (amount: number | string, decimals = 2): string => {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  if (isNaN(numAmount)) {
    return '0,00';
  }

  // Format with fixed decimals
  const fixed = numAmount.toFixed(decimals);
  const [integerPart, decimalPart] = fixed.split('.');

  // Add thousands separators (non-breaking space)
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  // Return with comma as decimal separator
  return `${formattedInteger},${decimalPart}`;
};

/**
 * Formats a date to DD.MM.YYYY format
 * @param date - Date object or ISO string
 * @returns Formatted date string
 * @example formatDate(new Date('2026-03-16')) => "16.03.2026"
 * @example formatDate('2026-03-16T10:30:00Z') => "16.03.2026"
 */
export const formatDate = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(d.getTime())) {
    return '';
  }

  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();

  return `${day}.${month}.${year}`;
};
